import { z } from 'zod';

/**
 * Proving a contact detail, and the second factor built on top of it.
 * ---------------------------------------------------------------------------
 * These two features are in one file because the second cannot exist without
 * the first. Two-factor authentication over email or SMS is only a factor if
 * the platform knows the address or the number actually reaches the person —
 * and until now nothing here has ever known that. An address is whatever the
 * administrator who created the account typed, which is an assertion, not a
 * proof. So: contact verification first, 2FA on top of it, and the enrolment
 * endpoint refuses a channel the person has not proved.
 *
 * ── Why a CODE and not a link ─────────────────────────────────────────────
 *
 * The established shape for an email change is a link. This platform uses a
 * six-digit code for both channels instead, for three reasons that all point
 * the same way:
 *
 *   - SMS cannot carry a link that proves anything. A number is verified by
 *     typing back what arrived, and having one mechanism for email and a
 *     different one for SMS means two sets of expiry rules, two attempt
 *     counters and two ways to get it wrong.
 *   - A link is a GET, and a GET is followed by mail scanners, link
 *     prefetchers and corporate security appliances. A single-use token that a
 *     scanner burns before the person clicks it is a support ticket per send.
 *   - The mobile app has to complete this flow too. A code is typed into
 *     whatever is already on screen; a link needs a deep link, a browser
 *     handoff, or a custom scheme — none of which exist here yet.
 *
 * The cost is that somebody must copy six digits. That is the whole cost.
 */

// --- Contact channels --------------------------------------------------------

export const CONTACT_CHANNELS = ['email', 'phone'] as const;
export const contactChannelSchema = z.enum(CONTACT_CHANNELS);
export type ContactChannel = z.infer<typeof contactChannelSchema>;

export const CONTACT_CHANNEL_LABELS: Record<ContactChannel, string> = {
  email: 'Email address',
  phone: 'Phone number',
};

/** How long a verification code is good for, and how much abuse it tolerates. */
export const CONTACT_CODE_TTL_MS = 15 * 60 * 1000;
export const MAX_CODE_ATTEMPTS = 5;
/** Codes per request, the first one included. Four more is three resends. */
export const MAX_CODE_SENDS = 4;
/** A resend before this has elapsed is refused — it is a send button, not a tap target. */
export const CODE_RESEND_INTERVAL_MS = 30 * 1000;

/**
 * Whether a channel can carry anything at all right now.
 *
 * The platform ships with SMTP off and the SMS gateway off, and most installs
 * stay that way. Every endpoint here consults this BEFORE it changes anything,
 * so an unconfigured channel produces a plain sentence and an untouched
 * account rather than a pending change nobody can ever complete.
 */
export const channelAvailabilitySchema = z.object({
  /** True when this platform could send on this channel this second. */
  available: z.boolean(),
  /** Why not, in words an operator can act on. Empty when available. */
  reason: z.string(),
});
export type ChannelAvailability = z.infer<typeof channelAvailabilitySchema>;

/** One channel's state for the signed-in person. */
export const contactStateSchema = z.object({
  channel: contactChannelSchema,
  /** The value on the account right now. Null when the account carries none. */
  value: z.string().nullable(),
  /** When it was last proved, or null if it never was. */
  verifiedAt: z.number().nullable(),
  /** Whether the platform can send on this channel at all. */
  delivery: channelAvailabilitySchema,
  /** A change that has been asked for and not yet proved. */
  pending: z
    .object({
      /** Masked — the full new value is echoed too, since the owner typed it. */
      newValue: z.string(),
      expiresAt: z.number(),
      attemptsLeft: z.number(),
      resendsLeft: z.number(),
      /** Earliest moment another code may be sent. */
      resendableAt: z.number(),
    })
    .nullable(),
});
export type ContactState = z.infer<typeof contactStateSchema>;

export const contactStatusSchema = z.object({
  email: contactStateSchema,
  phone: contactStateSchema,
});
export type ContactStatus = z.infer<typeof contactStatusSchema>;

/**
 * Starting a change — or a re-proof of the value already on the account.
 *
 * `currentPassword` is required on BOTH channels, not just on the address you
 * sign in with. The phone number is about to become an authentication factor,
 * so quietly repointing it is the same attack as repointing the email with one
 * extra step, and "the sensitive one needs the password" is a rule that only
 * works if it is not a judgement call at each call site.
 */
export const startContactChangeSchema = z.object({
  channel: contactChannelSchema,
  /** The new address or number. Passing the current one re-proves it. */
  value: z.string().min(1).max(200),
  currentPassword: z.string().min(1).max(200),
});
export type StartContactChangeRequest = z.infer<typeof startContactChangeSchema>;

export const verifyContactSchema = z.object({
  channel: contactChannelSchema,
  code: z.string().min(1).max(16),
});
export type VerifyContactRequest = z.infer<typeof verifyContactSchema>;

export const contactChannelOnlySchema = z.object({ channel: contactChannelSchema });
export type ContactChannelOnlyRequest = z.infer<typeof contactChannelOnlySchema>;

/** What a start/resend answers with: where it went, and how long you have. */
export const contactChallengeSchema = z.object({
  channel: contactChannelSchema,
  /** Masked destination, e.g. `b••@example.com` or `+2547•••••78`. */
  sentTo: z.string(),
  expiresAt: z.number(),
  attemptsLeft: z.number(),
  resendsLeft: z.number(),
  resendableAt: z.number(),
  /**
   * Whether the OLD value was told this was requested. False when there was no
   * old value, or when the change is a re-proof of the same one.
   */
  oldValueNotified: z.boolean(),
});
export type ContactChallenge = z.infer<typeof contactChallengeSchema>;

// --- Two-factor authentication ----------------------------------------------

/**
 * The channels a second factor can arrive on.
 *
 * `sms` rather than `phone` because this names a delivery mechanism, while the
 * contact channel names a field on the account. They map one to one, and
 * `mfaMethodChannel` below is the only place that conversion is written.
 */
export const MFA_METHODS = ['email', 'sms'] as const;
export const mfaMethodSchema = z.enum(MFA_METHODS);
export type MfaMethod = z.infer<typeof mfaMethodSchema>;

export const MFA_METHOD_LABELS: Record<MfaMethod, string> = {
  email: 'Email',
  sms: 'SMS',
};

export function mfaMethodChannel(method: MfaMethod): ContactChannel {
  return method === 'email' ? 'email' : 'phone';
}

/** Login codes live shorter than contact codes: somebody is waiting for this one. */
export const MFA_CHALLENGE_TTL_MS = 5 * 60 * 1000;
/** How many recovery codes are minted at enrolment, and again on regeneration. */
export const RECOVERY_CODE_COUNT = 10;

/**
 * A challenge in flight, as the client sees it.
 *
 * `token` is the half-authenticated handle: it stands in for "this person has
 * proved the password and nothing else". It is not a session, it cannot reach
 * any other endpoint, and it dies in five minutes. The client holds it in
 * memory for the length of one form.
 */
export const mfaChallengeViewSchema = z.object({
  token: z.string(),
  method: mfaMethodSchema,
  sentTo: z.string(),
  /**
   * False when the platform could not send the code — SMTP switched off since
   * enrolment, the gateway out of credit, the number no longer routable. The
   * challenge is NOT waived; `recoveryAvailable` is what answers it instead.
   */
  delivered: z.boolean(),
  /** Why nothing arrived, when `delivered` is false. */
  deliveryNote: z.string().nullable(),
  expiresAt: z.number(),
  attemptsLeft: z.number(),
  resendsLeft: z.number(),
  resendableAt: z.number(),
  /** Whether this account still has an unused recovery code to fall back on. */
  recoveryAvailable: z.boolean(),
});
export type MfaChallengeView = z.infer<typeof mfaChallengeViewSchema>;

/** What `POST /api/auth/login` returns when the password was right but is not enough. */
export const mfaRequiredSchema = z.object({
  mfaRequired: z.literal(true),
  challenge: mfaChallengeViewSchema,
});
export type MfaRequired = z.infer<typeof mfaRequiredSchema>;

/**
 * Distinguishes the two things a login can answer.
 *
 * Deliberately a FIELD TEST rather than a changed response shape: a client
 * written before 2FA existed still receives exactly the body it always did
 * when the account has no second factor. Only an enrolled account produces the
 * other shape, and only for people who chose to enrol.
 */
export function isMfaRequired(body: unknown): body is MfaRequired {
  return typeof body === 'object' && body !== null && (body as MfaRequired).mfaRequired === true;
}

export const mfaAnswerSchema = z.object({
  token: z.string().min(1).max(200),
  /** Either the six-digit code or one of the recovery codes. */
  code: z.string().min(1).max(64),
});
export type MfaAnswerRequest = z.infer<typeof mfaAnswerSchema>;

export const mfaResendSchema = z.object({ token: z.string().min(1).max(200) });
export type MfaResendRequest = z.infer<typeof mfaResendSchema>;

export const mfaEnrollSchema = z.object({
  method: mfaMethodSchema,
  currentPassword: z.string().min(1).max(200),
});
export type MfaEnrollRequest = z.infer<typeof mfaEnrollSchema>;

export const mfaPasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
});
export type MfaPasswordRequest = z.infer<typeof mfaPasswordSchema>;

/** Whether a method can be enrolled right now, and if not, exactly what is missing. */
export const mfaMethodOfferSchema = z.object({
  method: mfaMethodSchema,
  /** True only when the channel is configured AND this person has proved theirs. */
  eligible: z.boolean(),
  /** The contact value, masked. Null when the account carries none. */
  destination: z.string().nullable(),
  /** Whether the person has proved that contact detail. */
  contactVerified: z.boolean(),
  /** Whether the platform can send on this channel at all. */
  delivery: channelAvailabilitySchema,
  /** The single sentence to show when `eligible` is false. */
  blockedReason: z.string().nullable(),
});
export type MfaMethodOffer = z.infer<typeof mfaMethodOfferSchema>;

export const mfaStatusSchema = z.object({
  enabled: z.boolean(),
  method: mfaMethodSchema.nullable(),
  enrolledAt: z.number().nullable(),
  /** Unused recovery codes remaining. Zero on an account with 2FA off. */
  recoveryCodesLeft: z.number(),
  /** Every method, whether or not it can be chosen, with the reason. */
  methods: z.array(mfaMethodOfferSchema),
});
export type MfaStatus = z.infer<typeof mfaStatusSchema>;

/**
 * Recovery codes, at the ONE moment they are ever readable.
 *
 * They are hashed the instant they are stored, so this response is the only
 * copy that will ever exist. Both the API docs and the screen say so.
 */
export const recoveryCodesSchema = z.object({
  codes: z.array(z.string()),
  generatedAt: z.number(),
});
export type RecoveryCodesResponse = z.infer<typeof recoveryCodesSchema>;

// --- Masking -----------------------------------------------------------------

/**
 * `barnabie@example.com` -> `b••••••@example.com`.
 *
 * Shown BEFORE the second factor is answered, i.e. to somebody holding only a
 * password, so it must confirm the address to its owner without describing it
 * to anybody else. The domain stays whole on purpose: it is what tells the
 * owner which mailbox to open, and it is not the secret half.
 */
export function maskEmail(address: string): string {
  const at = address.lastIndexOf('@');
  if (at <= 0) return '•'.repeat(Math.max(address.length, 3));
  const local = address.slice(0, at);
  const domain = address.slice(at);
  const head = local.slice(0, 1);
  return `${head}${'•'.repeat(Math.max(local.length - 1, 1))}${domain}`;
}

/** `+254712345678` -> `+254•••••678`. Country code and last three, nothing else. */
export function maskPhone(number: string): string {
  const trimmed = number.trim();
  if (trimmed.length <= 4) return '•'.repeat(Math.max(trimmed.length, 3));
  const head = trimmed.slice(0, Math.min(4, trimmed.length - 3));
  const tail = trimmed.slice(-3);
  return `${head}${'•'.repeat(Math.max(trimmed.length - head.length - 3, 1))}${tail}`;
}

export function maskContact(channel: ContactChannel, value: string): string {
  return channel === 'email' ? maskEmail(value) : maskPhone(value);
}
